import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Plus, Search, Copy, Trash2,
  CheckCircle, Clock, AlertCircle, Brain
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserPartner } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

interface AssessmentCode {
  id: string;
  code: string;
  coachId: string;
  coachName?: string;
  isUsed: boolean;
  usedBy?: string;
  createdAt: Date;
  usedAt?: Date;
}

export function PartnerAssessmentCodesTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState<AssessmentCode[]>([]);
  const [coaches, setCoaches] = useState<{ id: string; name: string; userId: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    coachId: '',
    quantity: 1,
    autoAiAnalysis: false,
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/tr/login');
          return;
        }

        const { data: partner } = await supabase
          .from('partners')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!partner) {
          navigate('/tr/login');
          return;
        }

        setPartnerId(partner.id);
        await loadData(partner.id);
      } catch (err) {
        console.error('Error checking partner status:', err);
        navigate('/tr/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadData = async (pId: string) => {
    try {
      const { data: coachesData } = await supabase
        .from('coaches')
        .select('id, user_id, users!coaches_user_id_fkey(full_name, email)')
        .eq('partner_id', pId);

      const mappedCoaches = (coachesData || []).map((c: any) => ({
        id: c.id,
        name: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
        userId: c.user_id
      }));
      setCoaches(mappedCoaches);

      const coachIds = mappedCoaches.map((c: any) => c.id);
      if (coachIds.length === 0) {
        setCodes([]);
        return;
      }

      const { data: codesData } = await supabase
        .from('assessment_codes')
        .select('*')
        .eq('partner_id', pId);

      const mapped = (codesData || []).map((code: any) => {
        const coach = mappedCoaches.find((c: any) => c.id === code.coach_id);
        return {
          id: code.id,
          code: code.code,
          coachId: code.coach_id || '',
          coachName: coach?.name,
          isUsed: code.is_used || false,
          usedBy: code.used_by || undefined,
          createdAt: new Date(code.created_at || Date.now()),
          usedAt: code.used_at ? new Date(code.used_at) : undefined
        };
      }) as AssessmentCode[];

      setCodes(mapped.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Veriler yuklenemedi');
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!partnerId || !formData.coachId) return;

    try {
      const codesToInsert = Array.from({ length: formData.quantity }, () => ({
        code: generateCode(),
        coach_id: formData.coachId,
        partner_id: partnerId,
        is_used: false,
        auto_ai_analysis: formData.autoAiAnalysis,
      }));

      const { error: insertError } = await supabase
        .from('assessment_codes')
        .insert(codesToInsert);

      if (insertError) throw insertError;

      await loadData(partnerId);
      setShowAddModal(false);
      setFormData({ coachId: '', quantity: 1, autoAiAnalysis: false });
    } catch (err) {
      console.error('Error generating codes:', err);
      setError('Kodlar olusturulamadi');
    }
  };

  const handleDelete = async (codeId: string) => {
    if (!confirm('Bu kodu silmek istediginizden emin misiniz?')) return;
    if (!partnerId) return;

    try {
      const { error: deleteError } = await supabase
        .from('assessment_codes')
        .delete()
        .eq('id', codeId);

      if (deleteError) throw deleteError;
      await loadData(partnerId);
    } catch (err) {
      console.error('Error deleting code:', err);
      setError('Kod silinemedi');
    }
  };

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const filteredCodes = codes.filter(code =>
    code.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    code.coachName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Degerlendirme kodlari yukleniyor..." />;
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-coral-500 rounded-xl shadow-soft">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-chocolate-900">Degerlendirme Kodlari</h1>
                <p className="text-sm text-chocolate-500">{codes.length} toplam kod</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="freud-btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Kod Olustur
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="freud-card-elevated mb-6">
          <div className="p-4 border-b border-cream-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
              <input
                type="text"
                placeholder="Kod ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="freud-input pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream-100">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Kod</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Koc</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Durum</th>
                  <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Olusturulma</th>
                  <th className="text-right px-6 py-3 text-sm font-semibold text-chocolate-700">Islemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-200">
                {filteredCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-cream-50">
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-cream-200 rounded font-mono text-sm text-chocolate-900">
                        {code.code}
                      </code>
                    </td>
                    <td className="px-6 py-4 text-chocolate-600">
                      {code.coachName || 'Bilinmiyor'}
                    </td>
                    <td className="px-6 py-4">
                      {code.isUsed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-coral-100 text-coral-700 rounded-full text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Kullanildi
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-sage-100 text-sage-700 rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          Kullanilabilir
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-chocolate-600">
                      {code.createdAt.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyCode(code.code)}
                          className="p-2 text-chocolate-600 hover:bg-cream-200 rounded-lg transition-colors"
                        >
                          {copiedCode === code.code ? (
                            <CheckCircle className="w-4 h-4 text-sage-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        {!code.isUsed && (
                          <button
                            onClick={() => handleDelete(code.id)}
                            className="p-2 text-coral-600 hover:bg-coral-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCodes.length === 0 && (
            <div className="text-center py-12 text-chocolate-400">
              Henuz kod yok
            </div>
          )}
        </div>
      </main>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="freud-card-elevated p-6 w-full max-w-md m-4"
          >
            <h2 className="text-xl font-bold text-chocolate-900 mb-6">
              Degerlendirme Kodlari Olustur
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Koca Ata
                </label>
                <select
                  value={formData.coachId}
                  onChange={(e) => setFormData({ ...formData, coachId: e.target.value })}
                  required
                  className="freud-input"
                >
                  <option value="">Koc sec...</option>
                  {coaches.map(coach => (
                    <option key={coach.id} value={coach.id}>{coach.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-chocolate-700 mb-1">
                  Kod sayisi
                </label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  required
                  className="freud-input"
                />
              </div>
              <div className="bg-cream-100 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-sage-100 rounded-lg">
                      <Brain className="w-5 h-5 text-sage-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-chocolate-800">Otomatik YZ Analizi</p>
                      <p className="text-xs text-chocolate-500">
                        Katilimcilar aninda YZ analizi alir
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, autoAiAnalysis: !formData.autoAiAnalysis })}
                    className="flex-shrink-0"
                  >
                    <div
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        formData.autoAiAnalysis ? 'bg-sage-500' : 'bg-gray-300'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                          formData.autoAiAnalysis ? 'translate-x-[22px]' : 'translate-x-0.5'
                        }`}
                      />
                    </div>
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setFormData({ coachId: '', quantity: 1, autoAiAnalysis: false }); }}
                  className="freud-btn-secondary flex-1"
                >
                  Iptal
                </button>
                <button
                  type="submit"
                  className="freud-btn-primary flex-1"
                >
                  Olustur
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
